/*
 * @Author: Raeesaa Metkari
 * @Date: 2020-03-23 14:33:58
 * @Last Modified by: Raeesaa Metkari
 * @Last Modified time: 2020-08-03 21:10:01
 */
import { CircularLoading } from '@components/UI-Components/CircularLoading'
import { ConfirmationDialog } from '@components/UI-Components/ConfirmationDialog'
import { MessageDialog } from '@components/UI-Components/MessageDialog'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { PopupSize } from '@components/UI-Components/StandardPopup'
import { StandardTab } from '@components/UI-Components/StandardTabBar'
import { config } from '@config'
import { ProductGridContainer } from '@modules/collection/components/ProductGridContainer'
import { ProductListContainer } from '@modules/collection/components/ProductListContainer'
import {
  BuyingSessionAssortmentType,
  ChannelSubSection,
  Views,
  ViewTabs
} from '@modules/common'
import { useBuyingSessionGroup } from '@modules/wholesale/buyingGroupSummary/graphql/hooks'
import { useBuyingSession } from '@modules/wholesale/buyingSessionCollection/graphql/hooks'
import { WSProductDetailContainer } from '@modules/wholesale/productDetail/container/ProductDetail'
import { IPermissions } from '@routes'
import { isWholesaleChannel } from '@services/wholesale/wholesaleBuyingSessionGroupsServices'
import { wholesaleCollectionService } from '@services/wholesale/wholesaleCollectionService'
import { stores, strings } from '@stores'
import isEmpty from 'lodash/isEmpty'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { BulkOperationPopupContent } from '../component/BulkOperationPopup/BulkOperationPopupContent'
import { useBuyingSessionProductSummary } from '../graphql/hooks'
import * as css from './BuyingSessionProductContainerWrapper.css'
import { isAdmin, isCMAdmin } from '@services/commonServices'
import { productForecastUIStore } from '@modules/wholesale/buyingSessionGroupProductWrapper/stores/productForecastUIStore'
import { counterOfferUIStore } from '../stores/CounterOffer/CounterOfferUIStore'
import { ForecastTopBarOtbTotals } from '@components/UI-Components/BuyingToolBarRightMenu/components/ForecastTopBarOtbTotals'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { useTranslation } from 'react-i18next'
import { map } from 'lodash'
import { DefaultConfig } from '@constants'
import { TaggingDialog } from './TaggingDialog'
import { EnhancedConfirmationDialog } from '@components/UI-Components/EnhancedConfirmationDialog'
import { CentricTypography } from '@core/component/CentricTypography'

interface BuyingSessionGroupProductWrapper {
  permissions: IPermissions
}

/**
 * NOTE: We will be using this same container for both buying session group and buying session
 */
export const BuyingSessionProductContainerWrapper: React.FunctionComponent<
  BuyingSessionGroupProductWrapper
> = ({ permissions }) => {
  return useObserver(function useHook() {
    const {
      nav: {
        buyingSession,
        queryParams: {
          productLayout,
          buyingSession: buyingSessionId,
          bspId,
          buyingSessionGroupId,
          tab
        },
        updateQueryParams
      },
      wsBuyingSessionProduct: {
        whereInputForBSPSummaryForGroup,
        whereInputForBSPSummaryForBS,
        openMessageDialog,
        displayMessage,
        handleMessageDialogClose,
        openConfirmationDialog,
        handleConfirmationDialogClose,
        isDeleteInProgress,
        displayTitle,
        onDeleteConfirmationClick,
        onListViewTabChangeHandler,
        getTabsForView,
        isBulkOpPopupDisplayed,
        hideBulkOpPopupAndResetSelections,
        triggerBulkOpMutation,
        isAnyEntityDraggedForBulkOp,
        mouseDownEventHandlerForCollection,
        filterProductsBasedOnFilterAndSearch,
        updateProductsRefetchQuery,
        setBuyingSessionPresent,
        fetchBuyKPIs,
        productForecastKPIData,
        productBuyKPIData,
        isKPIDataLoading,
        retailBuyProducts,
        isBulkOpSaveActive,
        bulkOpSaveProgress,
        bulkOpSaveError,
        resetBulkOpData,
        resetBulkOpError,
        productSelectionStore: {
          openClearSelelctionWarningDilaog,
          clearSelectionCallBack,
          toggleOpenClearSelelctionWarningDilaog,
          setClearSelectionCallBack,
          resetBulkActionSelectedProducts,
          setAllProductsList,
          generateFilteredProductsHierarchy
        }
      },
      search: { searchKey, clearSearchKey },
      filterModalStore: { wsAppliedFilters },
      attributeSortStore: { selectedSortType },
      nav: {
        queryParams: { assortmentType, assortment, subSection }
      }
    } = stores
    const { totalForecastData, productForecastDataObjects } = productForecastUIStore
    const { getZoneColumnMap } = counterOfferUIStore
    const refNode = React.createRef<HTMLDivElement>()

    const { t } = useTranslation()
    const inputObject = buyingSession
      ? whereInputForBSPSummaryForBS
      : whereInputForBSPSummaryForGroup

    const {
      data,
      loading: bspSummaryLoading,
      refetch: summaryRefetch
    } = useBuyingSessionProductSummary(inputObject, 'cache-and-network')

    updateProductsRefetchQuery(summaryRefetch)

    setBuyingSessionPresent(data?.findVIPBuyingSessionProduct?.Items?.length > 0)

    React.useEffect(() => {
      // This is one time thing happening when we load all products data
      setAllProductsList(data?.findVIPBuyingSessionProduct?.Items)
    }, [data, setAllProductsList])

    const products = useMemo(() => {
      return filterProductsBasedOnFilterAndSearch(
        data?.findVIPBuyingSessionProduct?.Items ?? [],
        searchKey,
        wsAppliedFilters
      )
    }, [data, searchKey, wsAppliedFilters, filterProductsBasedOnFilterAndSearch])

    useEffect(() => {
      resetBulkActionSelectedProducts()
    }, [searchKey, wsAppliedFilters, resetBulkActionSelectedProducts])

    React.useEffect(() => {
      generateFilteredProductsHierarchy(products)
    }, [products, generateFilteredProductsHierarchy])

    let hierarchyKPIs =
      productLayout === strings.kpi
        ? tab === ViewTabs.FORECAST.toLowerCase()
          ? wholesaleCollectionService.buildProductForecastKPIHierarchy(
              productForecastKPIData as any[],
              products as any[]
            )
          : wholesaleCollectionService.processBuyKPIResponse(productBuyKPIData as any[])
        : wholesaleCollectionService.buildKPIHierarhyForBuyingSessionProducts(
            products as any[],
            false,
            null,
            null,
            {
              targetCurrency: config.globalCurrency || DefaultConfig.GLOBAL_CURRENCY
            } as any,
            null,
            null,
            null,
            totalForecastData,
            retailBuyProducts,
            tab,
            t,
            selectedSortType,
            productForecastDataObjects
          )

    React.useEffect(() => {
      if (productLayout === strings.list && !tab) {
        updateQueryParams({
          tab: isWholesaleChannel(bsgData?.buyingSessionGroup?.channels)
            ? ViewTabs.DISTRIBUTION.toLowerCase()
            : ViewTabs.RTL_DISTRIBUTION.toLowerCase()
        })
      }
      if (productLayout === strings.grid) {
        updateQueryParams({
          tab: undefined
        })
      }
      if (productLayout === strings.kpi) {
        fetchBuyKPIs(buyingSessionGroupId, buyingSessionId)
        updateQueryParams({
          tab: ViewTabs.FORECAST.toLowerCase()
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyingSessionGroupId, productLayout, buyingSessionId, selectedSortType])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(clearSearchKey, [])

    React.useEffect(() => {
      return () => {
        resetBulkOpData()
      }
    }, [resetBulkOpData])

    const bsData = useBuyingSession(buyingSessionId, 'cache-and-network')
    const { data: bsgData } = useBuyingSessionGroup(buyingSessionGroupId)
    const { tabList, selectedTab, tabColDefinition } = getTabsForView(
      bsgData?.buyingSessionGroup?.channels,
      getZoneColumnMap(
        bsData?.data?.getVIPBuyingSession?.STD_RTLBuyingSession_Status ===
          config?.appConfig?.enumerations?.retailBuyingSessionStatus
            ?.BUYING_SESSION_STATUS_VALUES.PRE_BUYING
      ),
      [bsData?.data?.getVIPBuyingSession?.STD_RTLBuyingSession_Status]
    )
    const isBuyingSessionClosed =
      bsData?.data?.getVIPBuyingSession?.Status ===
        config?.appConfig?.enumerations?.buyingSessionStatus?.BUYING_SESSION_STATUS_VALUES
          .CLOSED &&
      bsData?.data?.getVIPBuyingSession?.STD_RTLBuyingSession_Status ===
        config?.appConfig?.enumerations?.retailBuyingSessionStatus
          ?.BUYING_SESSION_STATUS_VALUES.CLOSED

    if (isEmpty(hierarchyKPIs.categories)) {
      if (!bspSummaryLoading) {
        return <NoRecordFound marginLeft={'23px'} />
      }
      return <CircularLoading />
    }

    const bulkPopupSaveHandler = () => {
      triggerBulkOpMutation({ bsgId: buyingSessionGroupId })
    }

    const showForecastQuickReporting =
      ViewTabs.FORECAST.toLowerCase() === tab &&
      productLayout === Views.List &&
      assortmentType !== BuyingSessionAssortmentType.cluster &&
      (subSection ? subSection === ChannelSubSection.Retail && assortment : true)
    const excludedClientIds = map(bsgData?.buyingSessionGroup.ExcludedClients || [], 'Id')

    const bulkActionPopup = isBulkOpPopupDisplayed ? (
      <TaggingDialog
        cancelHandler={hideBulkOpPopupAndResetSelections}
        saveHandler={bulkPopupSaveHandler}
        bulkOpSaveProgress={bulkOpSaveProgress}
        maxWidth={
          bsgData?.buyingSessionGroup?.channels?.length > 1 && (isAdmin() || isCMAdmin())
            ? PopupSize.extraLarge
            : PopupSize.medium
        }
        isBulkOperation={true}
        isBulkOpSaveActive={isBulkOpSaveActive}
        bulkOpSaveError={!!bulkOpSaveError}
        disableSave={
          !isAnyEntityDraggedForBulkOp || isBuyingSessionClosed || isBulkOpSaveActive
        }
      >
        <BulkOperationPopupContent
          selectedChannels={bsgData?.buyingSessionGroup?.channels}
          buyingSessionGroupStatuses={{
            [VIPChannel.Wholesale]: bsgData?.buyingSessionGroup?.BuyingSessionGroupStatus,
            [VIPChannel.Retail]:
              bsgData?.buyingSessionGroup?.STD_RTLBuyingSessionGroup_Status
          }}
          bsgExcludedClientIds={excludedClientIds as [string]}
        />
      </TaggingDialog>
    ) : null

    return (
      <>
        <div
          ref={refNode}
          className={css.wsCollectionContainerWrapper}
          onMouseDown={mouseDownEventHandlerForCollection(refNode)}
        >
          {productLayout === strings.grid ? (
            <div className={css.wsCollectionContainer}>
              <ProductGridContainer
                hierarchyKPIs={hierarchyKPIs}
                isWholeSaleRequest={true}
              />
            </div>
          ) : (
            <>
              <div className={css.wsListViewTabContainer}>
                <StandardTab
                  tabList={tabList}
                  selectedTabValue={selectedTab}
                  onTabChangeHandler={onListViewTabChangeHandler}
                ></StandardTab>
              </div>
              {isKPIDataLoading && <CircularLoading />}
              {tabColDefinition &&
                !isEmpty(bsgData?.buyingSessionGroup?.channels) &&
                !isKPIDataLoading && (
                  <div className={css.wsCollectionContainer}>
                    <ProductListContainer
                      hierarchyKPIs={hierarchyKPIs}
                      colDef={tabColDefinition}
                      buyingSessionGroupName={bsgData?.buyingSessionGroup?.Name}
                      channels={bsgData?.buyingSessionGroup?.channels}
                      buyingSessionGroupStatuses={{
                        [VIPChannel.Retail]:
                          bsgData?.buyingSessionGroup?.STD_RTLBuyingSessionGroup_Status
                      }}
                    ></ProductListContainer>
                  </div>
                )}
            </>
          )}
          {openMessageDialog ? (
            <MessageDialog
              open={openMessageDialog}
              title={t('bb.common.notAllowed', 'Not Allowed')}
              message={displayMessage}
              handleClose={handleMessageDialogClose}
            />
          ) : null}
          {openConfirmationDialog ? (
            <ConfirmationDialog
              open={openConfirmationDialog}
              title={displayTitle}
              message={displayMessage}
              handleClose={handleConfirmationDialogClose}
              handleDelete={onDeleteConfirmationClick}
              proceedButtonTitle={t('bb.common.yes', 'Yes')}
              cancelButtonTitle={t('bb.common.label.no', 'No')}
              loading={isDeleteInProgress}
            />
          ) : null}
          {bulkActionPopup}
          {!!bulkOpSaveError ? (
            <EnhancedConfirmationDialog
              key='bulkOpSaveErrorDialog'
              open={!!bulkOpSaveError}
              title={
                <CentricTypography variant='h4'>
                  {t('bb.bulkProductOps.saveFailed', 'Save Failed!')}
                </CentricTypography>
              }
              bodyContent={
                <CentricTypography variant='body1'>
                  {t(
                    'bb.bulkProductOps.couldNotSave',
                    "We Couldn't save your changes due to technical issue. \n Please contact support for assistance."
                  )}
                </CentricTypography>
              }
              hideConfirmButton={true}
              hideCancelButton={true}
              showBackButton={true}
              backButtonTitle={t('common.label.close', 'Close')}
              handleBack={() => {
                resetBulkOpError()
                hideBulkOpPopupAndResetSelections()
              }}
              handleClose={() => {
                resetBulkOpError()
                hideBulkOpPopupAndResetSelections()
              }}
            />
          ) : null}

          {openClearSelelctionWarningDilaog ? (
            <EnhancedConfirmationDialog
              key='openClearSelelctionWarningDilaog'
              open={openClearSelelctionWarningDilaog}
              title={
                <CentricTypography variant='h4'>
                  {t('bb.bulkProductOps.selectionsClear', 'Selections Will be Cleared')}
                </CentricTypography>
              }
              bodyContent={
                <CentricTypography variant='body1'>
                  {t(
                    'bb.bulkProductOps.filterClearConfirm',
                    'Applying new filters or performing a search will remove your current product selections. Do you want to continue?'
                  )}
                </CentricTypography>
              }
              proceedButtonTitle={t('common.label.continue', 'Continue')}
              cancelButtonTitle={t('common.label.cancel', 'Cancel')}
              handleClose={() => {
                setClearSelectionCallBack(null)
                toggleOpenClearSelelctionWarningDilaog()
              }}
              handleConfirm={e => {
                if (typeof clearSelectionCallBack === 'function') {
                  clearSelectionCallBack?.()
                }
                toggleOpenClearSelelctionWarningDilaog()
                setClearSelectionCallBack(null)
              }}
            />
          ) : null}
        </div>
        {bspId ? <WSProductDetailContainer /> : null}
        {showForecastQuickReporting ? <ForecastTopBarOtbTotals /> : null}
      </>
    )
  })
}
